// src/scenes/stockout/ViewStockOut.jsx
import React from "react";
import axios from "axios";
import Header from "../../components/Header";
import SimpleTable from "../../components/SimpleTable";
import { Formik, FieldArray } from "formik";
import * as yup from "yup";
import {
  Box,
  Snackbar,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Divider,
  TextField,
  MenuItem,
  useTheme,
  Menu,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { tokens } from "../../theme";
import PermissionGuard from "../../components/PermissionGuard";

/* ---------- axios ---------- */

const token = localStorage.getItem("token");
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 20000,
  headers: {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  },
});

/* ---------- API ---------- */
async function getJSON(path) {
  try {
    const res = await api.get(path, { params: { _t: Date.now() } });
    return { data: res.data?.data, error: null };
  } catch (err) {
    return {
      data: null,
      error: err?.response?.data?.message || err?.message || "Request failed",
    };
  }
}

async function deleteJSON(path) {
  try {
    const res = await api.delete(path);
    return { data: res.data, error: null };
  } catch (err) {
    return {
      data: null,
      error: err?.response?.data?.message || err?.message || "Delete failed",
    };
  }
}

async function putJSON(path, body) {
  try {
    const res = await api.put(path, body);
    return { data: res.data, error: null };
  } catch (err) {
    return {
      data: null,
      error: err?.response?.data?.message || err?.message || "Request failed",
    };
  }
}

/* ---------- Validation Schema for Edit Form ---------- */
const EDIT_SCHEMA = yup.object({
  userId: yup.number().required("Select user"),
  warehouseId: yup.number().required("Select warehouse"),
  description: yup.string().nullable(),

  items: yup.array().of(
    yup.object({
      itemId: yup.number().required("Select item"),

      quantity: yup
        .number()
        .typeError("Must be a number")
        .min(1)
        .required(),

      unitPrice: yup
        .number()
        .typeError("Must be a number")
        .min(0)
        .required(),
    })
  ),
});

/* ---------- Utility extractors ---------- */
const safe = (v, fallback = "") =>
  v === null || v === undefined ? fallback : v;

const userLabelFrom = (u) =>
  u ? u.fullName || u.name || u.userName || u.username || u.email || "-" : "-";

export default function ViewStockOut() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const lsUser = React.useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  }, []);

  const roleRaw = String(
    lsUser?.role || lsUser?.userRole || lsUser?.roleName || lsUser?.type || "",
  )
    .toLowerCase()
    .trim();

  const isSalesMan =
    /(sales\s*-?\s*man|salesman|sales\s*person|salesperson|^sales$)/i.test(
      roleRaw,
    );
  const currentUserId = String(
    lsUser?.id ?? lsUser?.userId ?? lsUser?._id ?? "",
  );
  const canEdit = !isSalesMan;

  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [toast, setToast] = React.useState({
    open: false,
    msg: "",
    sev: "success",
  });

  const [detailRow, setDetailRow] = React.useState(null);
  const [deleteRow, setDeleteRow] = React.useState(null);
  const [deleting, setDeleting] = React.useState(false);

  const [editRow, setEditRow] = React.useState(null);
  const [masterUsers, setMasterUsers] = React.useState([]);
  const [masterItems, setMasterItems] = React.useState([]);

  const [masterWarehouses, setMasterWarehouses] = React.useState([]);
  const mounted = React.useRef(true);

  // status inline menu
  const [statusMenu, setStatusMenu] = React.useState({
    anchorEl: null,
    row: null,
  });

  /* ---------- load main table data + master lists ---------- */
  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      let apiUrl = "/api/stockout";
      if (isSalesMan) apiUrl = `/api/stockout/user/${currentUserId}`;

      // Fetch stockout rows and master data in parallel
      const [stockRes, usersRes, itemsRes, warehousesRes] = await Promise.all([
        getJSON(apiUrl),
        getJSON("/api/user"),
        getJSON("/api/item"),

        getJSON("/api/warehouse"),
      ]);

      if (!mounted.current) return;

      if (stockRes.error) {
        setToast({ open: true, msg: stockRes.error, sev: "error" });
        setRows([]);
      } else {
        const data = Array.isArray(stockRes.data) ? stockRes.data : [];
        const mapped = data.map((r, i) => {
          const firstItem = r.items?.[0]?.item?.name || "-";

          const totalQuantity = (r.items || []).reduce(
  (sum, it) => sum + Number(it.quantity || 0),
  0
);

          return {
            ...r,
            rowNumber: i + 1,

            // user + warehouse (NEW STRUCTURE)
            userLabel: r.user?.fullName || "-",
            warehouseLabel: r.warehouse?.name || "-",

            // items
            itemName: firstItem,
            quantity: totalQuantity, 
          };
        });
        setRows(mapped);
      }

      // master lists (safe fallback)
      setMasterUsers(
        Array.isArray(usersRes.data)
          ? usersRes.data.filter(
              (u) => String(u?.Role?.name || "").toLowerCase() === "sales",
            )
          : [],
      );
      setMasterItems(Array.isArray(itemsRes.data) ? itemsRes.data : []);

      setMasterWarehouses(
        Array.isArray(warehousesRes.data) ? warehousesRes.data : [],
      );
    } catch (err) {
      setToast({ open: true, msg: "Failed to load data", sev: "error" });
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [currentUserId, isSalesMan]);

  React.useEffect(() => {
    mounted.current = true;
    load();
    const onFocus = () => load();
    const interval = setInterval(load, 30000);
    window.addEventListener("focus", onFocus);
    return () => {
      mounted.current = false;
      window.removeEventListener("focus", onFocus);
      clearInterval(interval);
    };
  }, [load]);

  /* ---------- Delete handler ---------- */
  const handleDelete = async () => {
    if (!deleteRow?.id) return;
    setDeleting(true);
    const prev = [...rows];
    setRows((r) => r.filter((x) => x.id !== deleteRow.id));
    const { error } = await deleteJSON(`/api/stockout/${deleteRow.id}`);
    setDeleting(false);
    if (error) {
      setRows(prev);
      setToast({ open: true, msg: error, sev: "error" });
      return;
    }
    setToast({ open: true, msg: "Deleted successfully", sev: "success" });
    setDeleteRow(null);
    await load();
  };

  /* ---------- Edit/Open dialog handlers ---------- */
  const openEdit = (row) => {
  const initials = {
    userId: row.userId || row.user?.id || "",
    warehouseId: row.warehouseId || row.warehouse?.id || "",
    description: row.description || "",

    items:
      row.items?.map((si) => ({
        itemId: Number(si.itemId),
        quantity: Number(si.quantity),
        unitPrice: Number(si.unitPrice || 0),
      })) || [],
  };

  console.log("EDIT INITIALS", initials);

  setEditRow({
    ...row,
    __formInitials: initials,
  });
};

  const closeEdit = () => setEditRow(null);

  /* ---------- DataGrid columns ---------- */
  const columns = [
    { field: "rowNumber", headerName: "No.", width: 80 },

    // { field: "itemName", headerName: "Item", flex: 1 },
    { field: "userLabel", headerName: "User", flex: 1 },

    { field: "quantity", headerName: "quantity", flex: 0.6 },

    { field: "warehouseLabel", headerName: "Warehouse", flex: 1 },
    {
      field: "status",
      headerName: "Status",
      width: 150,
      sortable: false,
      renderCell: (params) => {
        const row = params.row;
        const colorMap = {
          pending: "warning",
          approved: "success",
          rejected: "error",
        };
        const label = (row.status || "pending").toLowerCase();
        return (
          <Button
            size="small"
            variant="outlined"
            color={colorMap[label] || "inherit"}
            onClick={(e) => setStatusMenu({ anchorEl: e.currentTarget, row })}
          >
            {label}
          </Button>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 160,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <Tooltip title="Details">
            <IconButton size="small" onClick={() => setDetailRow(params.row)}>
              <InfoOutlinedIcon />
            </IconButton>
          </Tooltip>

          {canEdit && (
            <PermissionGuard module="Stock Out" action="update">
              <Tooltip title="Edit">
                <IconButton size="small" onClick={() => openEdit(params.row)}>
                  <EditIcon />
                </IconButton>
              </Tooltip>
            </PermissionGuard>
          )}
          <PermissionGuard module="Stock Out" action="delete">
          <Tooltip title="Delete">
            <IconButton
              size="small"
              color="error"
              onClick={() => setDeleteRow(params.row)}
            >
              <DeleteOutlineIcon />
            </IconButton>
          </Tooltip>
          </PermissionGuard>
        </Box>
      ),
    },
  ];

  const gridSx = {
    "& .MuiDataGrid-root": { border: "none" },
    "& .MuiDataGrid-columnHeaders": { backgroundColor: colors.blueAccent[700] },
    "& .MuiDataGrid-virtualScroller": { backgroundColor: colors.primary[400] },
    "& .MuiDataGrid-footerContainer": {
      backgroundColor: colors.blueAccent[700],
    },
  };

  /* ---------- Edit form submit ---------- */
 const submitEdit = async (values, { setSubmitting }) => {
  const payload = {
    userId: Number(values.userId),
    warehouseId: Number(values.warehouseId),
    description: values.description || "",
    items: values.items.map((it) => ({
      itemId: Number(it.itemId),
      quantity: Number(it.quantity),
      unitPrice: Number(it.unitPrice),
    })),
  };

  const { error } = await putJSON(`/api/stockout/${editRow.id}`, payload);

  setSubmitting(false);

  if (error) {
    setToast({ open: true, msg: error, sev: "error" });
    return;
  }

  setToast({ open: true, msg: "Updated successfully", sev: "success" });
  closeEdit();
  await load();
};

  const handleStatusChange = async (row, newStatus) => {
    if (!row?.id) return;

    if (row.status !== "pending") {
      setToast({ open: true, msg: "Status already changed.", sev: "error" });
      return;
    }

    const payload = { status: newStatus };

    // Optimistic UI update
    const prevRows = [...rows];
    setRows((prev) =>
      prev.map((r) => (r.id === row.id ? { ...r, status: newStatus } : r)),
    );

    // Use PATCH to the dedicated endpoint
    try {
      await api.patch(`/api/stockout/${row.id}/status`, payload);
      setToast({
        open: true,
        msg: `Status set to ${newStatus}`,
        sev: "success",
      });
      await load(); // reload rows to reflect updated stock quantities
    } catch (err) {
      setRows(prevRows);
      setToast({ open: true, msg: "Failed to update status", sev: "error" });
    }
  };

  const InfoRow = ({ label, value }) => (
    <Box sx={{ display: "flex", mb: 0.6 }}>
      <Typography sx={{ fontWeight: 600, mr: 1 }}>{label}:</Typography>
      <Typography>{value ?? "-"}</Typography>
    </Box>
  );

  return (
    <Box m="20px">
      <Header
        title="STOCK OUT"
        subtitle="View, edit, and manage stock out records"
      />

      <Box m="20px 0 0 0">
        <SimpleTable
          rows={rows}
          columns={columns}
          loading={loading}
          getRowId={(r) => r.id}
          sx={gridSx}
          height="70vh"
        />
      </Box>
      {/* Status Change Menu   */}

      <Menu
        anchorEl={statusMenu.anchorEl}
        open={Boolean(statusMenu.anchorEl)}
        onClose={() => setStatusMenu({ anchorEl: null, row: null })}
      >
        {["approved", "rejected"].map((opt) => (
          <MenuItem
            key={opt}
            disabled={!isSalesMan}
            onClick={async () => {
              if (!isSalesMan) return; // block clicks
              const row = statusMenu.row;
              setStatusMenu({ anchorEl: null, row: null });
              await handleStatusChange(row, opt);
            }}
          >
            {opt}
          </MenuItem>
        ))}
      </Menu>

      {/* Detail Dialog */}
      <Dialog
        open={!!detailRow}
        onClose={() => setDetailRow(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Stockout Details</DialogTitle>

        <DialogContent dividers sx={{ px: 3, py: 2 }}>
          {detailRow && (
            <Box>
              {/* SECTION TITLE */}
              <Typography variant="h6" sx={{ mb: 2 }}>
                Stockout Information
              </Typography>

              {/* CLEAN INFO ROWS */}
              <Box sx={{ mb: 3 }}>
                <InfoRow label="No." value={detailRow.rowNumber} />
                <InfoRow
                  label="User"
                  value={detailRow.userLabel || userLabelFrom(detailRow.User)}
                />
                <InfoRow
                  label="Warehouse"
                  value={
                    detailRow.warehouseLabel ||
                    detailRow.Warehouse?.name ||
                    detailRow.warehouseName
                  }
                />

                <InfoRow label="Amount (total)" value={detailRow.amount} />
                <InfoRow
                  label="Description"
                  value={
                    detailRow.description ||
                    detailRow.Description ||
                    "No additional details available."
                  }
                />
                <InfoRow
                  label="Created At"
                  value={
                    detailRow.createdAt
                      ? new Date(detailRow.createdAt).toLocaleString()
                      : "-"
                  }
                />
              </Box>

              {/* ITEMS SECTION */}
              <Typography variant="h6" sx={{ mb: 1 }}>
                Items
              </Typography>

              {detailRow.items?.length ? (
                <Box>
                  {detailRow.items.map((si, idx) => {
                    const itemName = si.item?.name || `Item #${si.itemId}`;

                    return (
                      <Box key={idx} sx={{ mb: 2 }}>
                        <InfoRow label="Item Name" value={itemName} />
                        <InfoRow label="Quantity" value={si.quantity ?? 0} />

                        {idx < detailRow.items.length - 1 && (
                          <Divider sx={{ my: 2 }} />
                        )}
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Typography>No items recorded.</Typography>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDetailRow(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={!!deleteRow}
        onClose={() => setDeleteRow(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Stockout</DialogTitle>
        <DialogContent dividers>
          <Typography>
            Are you sure you want to delete this stock-out record for{" "}
            <strong>{deleteRow?.itemName ?? "-"}</strong>{" "}
            {deleteRow?.createdAt
              ? `created at ${new Date(deleteRow.createdAt).toLocaleString()}`
              : ""}
            ?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteRow(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            disabled={deleting}
            color="error"
            variant="contained"
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editRow} onClose={closeEdit} maxWidth="md" fullWidth>
        <DialogTitle>Edit Stock-Out</DialogTitle>
        <DialogContent dividers>
          {editRow && (
            <Formik
             enableReinitialize
              initialValues={editRow.__formInitials}
              validationSchema={EDIT_SCHEMA}
              onSubmit={submitEdit}
            >
              {(bag) => (
                <form onSubmit={bag.handleSubmit}>
                  <Box
                    display="grid"
                    gap="20px"
                    gridTemplateColumns="repeat(4, minmax(0, 1fr))"
                    sx={{ mt: 1 }}
                  >
                    <TextField
                      select
                      label="User"
                      name="userId"
                      value={bag.values.userId}
                      onChange={bag.handleChange}
                      variant="filled"
                      fullWidth
                      error={!!(bag.touched.userId && bag.errors.userId)}
                      helperText={
                        bag.touched.userId && bag.errors.userId
                          ? bag.errors.userId
                          : ""
                      }
                    >
                      {masterUsers.map((u) => (
                        <MenuItem
                          key={u.id ?? u.userId}
                          value={u.id ?? u.userId}
                        >
                          {u.fullName || u.name || u.username || u.email}
                        </MenuItem>
                      ))}
                    </TextField>

                    <TextField
                      select
                      label="Warehouse"
                      name="warehouseId"
                      value={bag.values.warehouseId}
                      onChange={bag.handleChange}
                      variant="filled"
                      fullWidth
                      error={
                        !!(bag.touched.warehouseId && bag.errors.warehouseId)
                      }
                      helperText={
                        bag.touched.warehouseId && bag.errors.warehouseId
                          ? bag.errors.warehouseId
                          : ""
                      }
                    >
                      {masterWarehouses.map((w) => (
                        <MenuItem key={w.id} value={w.id}>
                          {w.name || w.warehouseName}
                        </MenuItem>
                      ))}
                    </TextField>

                    <TextField
                      label="Description (optional)"
                      name="description"
                      value={bag.values.description}
                      onChange={bag.handleChange}
                      variant="filled"
                      fullWidth
                      multiline
                      minRows={1}
                    />

                    {/* Items array - spans full width */}
                    <FieldArray name="items">
                      {({ push, remove }) => (
                        <Box gridColumn="span 4">
                          {bag.values.items.map((it, idx) => (
                            <Box
                              key={idx}
                              display="grid"
                              gridTemplateColumns="2fr 1fr 1fr 1fr 80px"
                              gap="10px"
                              mb={2}
                              alignItems="center"
                            >
                              <TextField
                                select
                                label="Item"
                                name={`items[${idx}].itemId`}
                                value={it.itemId}
                                onChange={bag.handleChange}
                                variant="filled"
                                fullWidth
                                error={
                                  !!(
                                    bag.touched.items &&
                                    bag.touched.items[idx] &&
                                    bag.errors.items &&
                                    bag.errors.items[idx] &&
                                    bag.errors.items[idx].itemId
                                  )
                                }
                                helperText={
                                  bag.touched.items &&
                                  bag.touched.items[idx] &&
                                  bag.errors.items &&
                                  bag.errors.items[idx] &&
                                  bag.errors.items[idx].itemId
                                    ? bag.errors.items[idx].itemId
                                    : ""
                                }
                              >
                                {masterItems.map((m) => (
                                  <MenuItem key={m.id} value={m.id}>
                                    {m.name || m.itemName}
                                  </MenuItem>
                                ))}
                              </TextField>

                              <TextField
                                type="number"
                                label="Quantity"
                                name={`items[${idx}].quantity`}
                                value={it.quantity}
                                onChange={bag.handleChange}
                                variant="filled"
                              />

                              <TextField
                                type="number"
                                label="Unit Price"
                                name={`items[${idx}].unitPrice`}
                                value={it.unitPrice}
                                onChange={bag.handleChange}
                                variant="filled"
                              />

                              <Button
                                type="button"
                                color="error"
                                onClick={() => remove(idx)}
                              >
                                X
                              </Button>
                            </Box>
                          ))}

                          <Button
                            type="button"
                            onClick={() =>
                              push({
                                itemId: "",
                                amount: 1,
                                sponsor: 0,
                                bonus: 0,
                              })
                            }
                          >
                            Add Item
                          </Button>
                        </Box>
                      )}
                    </FieldArray>
                  </Box>

                  <Box display="flex" justifyContent="end" mt="20px" gap={2}>
                    <Button onClick={() => bag.resetForm()} variant="outlined">
                      Reset
                    </Button>
                    <Button
                      type="submit"
                      color="secondary"
                      variant="contained"
                      disabled={bag.isSubmitting}
                    >
                      {bag.isSubmitting ? "Saving..." : "Save Changes"}
                    </Button>
                  </Box>
                </form>
              )}
            </Formik>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={closeEdit}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ mt: 5 }}
      >
        <Alert
          onClose={() => setToast((s) => ({ ...s, open: false }))}
          severity={toast.sev}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
